import { OrderedStep } from "@instacover/helpers"

import { dexie } from "../../db"
import { logDexieError } from "../../utils/logDexieError"

// ─── Types ─────────────────────────────────────────────────────────────────────

export type OrderedStepLocal = Omit<OrderedStep, "name" | "stepType"> & {
  name: OrderedStep["name"] | "GDPRConsent" | "Intro" | "DocumentsIntro" | "FinalCheck"
  stepType: OrderedStep["stepType"] | "GDPRConsent" | "Intro" | "DocumentsIntro" | "FinalCheck"
  dexieId: number
  sessionId: string
  isFinished: boolean
}

/** OrderedStepLocal narrowed to real steps (excludes Intro/FinalCheck virtual steps) */
export type RealOrderedStep = Omit<OrderedStepLocal, "name" | "stepType"> & {
  name: OrderedStep["name"]
  stepType: OrderedStep["stepType"]
}

export function isRealOrderedStep(step: OrderedStepLocal): step is RealOrderedStep {
  return (
    step.stepType !== "GDPRConsent" &&
    step.stepType !== "Intro" &&
    step.stepType !== "DocumentsIntro" &&
    step.stepType !== "FinalCheck"
  )
}

// ─── Methods ───────────────────────────────────────────────────────────────────

export async function upsertOrderedStep(data: Omit<OrderedStepLocal, "dexieId">): Promise<void> {
  try {
    await dexie.transaction("rw", dexie.orderedSteps, async () => {
      const existing = await dexie.orderedSteps.where("[name+sessionId]").equals([data.name, data.sessionId]).first()

      if (existing) {
        await dexie.orderedSteps.update(existing.dexieId, data)
      } else {
        await dexie.orderedSteps.add(data as OrderedStepLocal)
      }
    })
  } catch (error) {
    logDexieError(error)
  }
}

export async function findOrderedSteps(sessionId: string): Promise<OrderedStepLocal[]> {
  try {
    const steps = await dexie.orderedSteps.where("sessionId").equals(sessionId).toArray()
    return steps.sort((a, b) => a.dexieId - b.dexieId)
  } catch (error) {
    logDexieError(error)
    return []
  }
}

export async function findOrderedStepByNameAndSessionId(
  name: string,
  sessionId: string,
): Promise<OrderedStepLocal | undefined> {
  try {
    return await dexie.orderedSteps.where("[name+sessionId]").equals([name, sessionId]).first()
  } catch (error) {
    logDexieError(error)
  }
}

export async function modifyOrderedStep(
  name: string,
  sessionId: string,
  data: Partial<OrderedStepLocal>,
): Promise<void> {
  try {
    await dexie.orderedSteps.where("[name+sessionId]").equals([name, sessionId]).modify(data)
  } catch (error) {
    logDexieError(error)
  }
}

export async function findFirstUnfinishedOrderedStep(sessionId: string): Promise<OrderedStepLocal | undefined> {
  try {
    const allSteps = await dexie.orderedSteps.where("sessionId").equals(sessionId).toArray()
    return allSteps.sort((a, b) => a.dexieId - b.dexieId).find((step) => !step.isFinished)
  } catch (error) {
    logDexieError(error)
  }
}
